import { styled } from '@mui/material/styles';
import { Drawer, Icon, IconButton, List, ListItem, ListItemButton, ListItemIcon } from '@mui/material';
import type { CSSObject, ListItemButtonProps } from '@mui/material';
import type { ElementType } from 'react';
import type { Theme } from '@mui/material/styles';
import { Opacity } from '@mui/icons-material';

const openedMixin = (theme: Theme): CSSObject => ({
  width: '18.75rem',
  overflow: 'visible',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  })
});

const closedMixin = (theme: Theme): CSSObject => ({
  width: theme.spacing(0),
  overflow: 'visible',
  [theme.breakpoints.up('sm')]: {
    width: theme.spacing(12)
  },
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  })
});

export const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    borderRight: '1px solid',
    borderRightColor: theme.palette.grey[200],
    background: theme.palette.common.white,
    color: theme.palette.common.black,
    paddingBottom: theme.spacing(2),
    '& > .MuiListItem-root .Mui-selected': {}
  },
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme)
      }
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme)
      }
    }
  ]
}));

export const StyledList = styled(List)(({ theme }) => ({
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  overflow: 'auto',
  marginTop: theme.spacing(1)
}));

export const StyledListItem = styled(ListItemButton)<ListItemButtonProps<ElementType>>(({ theme }) => ({
  padding: theme.spacing(0.8, 2, 0.8, 2),
  whiteSpace: 'nowrap',
  minHeight: '3rem',
  display: 'flex',
  gap: theme.spacing(2),
  borderRadius: '6rem',
  '&.Mui-selected': {
    backgroundColor: theme.palette.secondary[100]
  },
  '&.Mui-selected:hover': {
    backgroundColor: theme.palette.primary[100]
  },
  '&:hover': {
    backgroundColor: theme.palette.primary[100]
  }
}));

export const StyledListItemIcon = styled(ListItemIcon)(({ theme }) => ({
  minWidth: theme.spacing(2),
  '& .MuiSvgIcon-root': {
    color: 'black'
  }
}));

export const StyledNestedListItem = styled(ListItem)(({ theme }) => ({
  paddingLeft: theme.spacing(4)
}));

export const StyledTextField = styled('div')(({ theme }) => ({
  width: 'calc(100% - 24px)',
  padding: theme.spacing(0, 0, 0, 1.5)
}));

export const StyledDivider = styled('hr')({
  borderBottomWidth: 2,
  borderBottomColor: '#ddd',
  borderBottomStyle: 'solid'
});

export const StyledLanguagePicker = styled('div')({
  padding: '4px 16px'
});

export const MenuToggleIconButton = styled(IconButton)(({ theme }) => ({
  height: 36,
  width: 36,
  position: 'absolute',
  top: 18,
  [theme.breakpoints.up('sm')]: {
    right: -20
  },
  color: theme.palette.common.white,
  backgroundColor: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    opacity: 0.85
  }
}));
